import React, { PureComponent, createRef } from 'react'
import cx from 'classnames'
import { Text, Box } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import { withRouter } from 'react-router'
import { injectIntl, FormattedMessage, FormattedHTMLMessage } from 'react-intl'

import { Layout, Container, Content, Footer, PageTitle } from 'src/components/Layout'
import List from 'src/components/List/List'
import Loader from 'src/components/Loader/Loader'
import EventsService from 'src/utils/events-service'
import { postRequest } from 'src/utils/request-client'
import {
  getErrorMessage,
  getErrorCode,
  logError,
  setLoginToken,
  isLoggedIn,
  getBrandSettings,
  timeSinceTimestamp,
  setChatIdentity,
  updateChatIdentity,
  getChatChannelName,
  setChatChannelName,
  updateChatChannelName,
  isInStandaloneMode,
  getFeatures,
  getRedirectUrl,
  removeRedirectUrl,
} from 'src/utils/helpers'
import { isColorRedOrVariant } from 'src/utils/colors'
import { WidgetContext } from 'src/modules/App/WidgetContext'

import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from '../translations'

import styles from './Email.module.less'

import { Input, Checkbox } from 'src/components/Form'
import Button from 'src/components/Button/Button'
import { validateEmail } from 'src/utils/validation'

import withToast from 'src/components/Toast'

const isStandalone = isInStandaloneMode()

const NUMBER_REGEX = /^[0-9]+$/

class Email extends PureComponent {
  static contextTypes = {
    router: PropTypes.object,
  }

  state = {
    submitting: false,
    emailSent: this.props.email,
    multipleAccounts: null,
    isEmailJustSent: false,
    usePin: true,
  }
  constructor(props) {
    super(props)

    this.pinFieldRef = createRef()
  }

  componentDidMount() {
    const {
      user,
      history,
      disableLoggedInRedirect,
      setEmailTimestamp,
      setAppActionsDisabledState,
      areAppActionsDisabled,
    } = this.props

    const { submitting } = this.state

    if (isLoggedIn(user) && !disableLoggedInRedirect && !submitting && !areAppActionsDisabled) {
      // Enable global app actions (menus, etc.)
      setAppActionsDisabledState(false)

      history.push('/')
      return true
    }

    if (timeSinceTimestamp(setEmailTimestamp, 'minutes') >= 60) {
      this.tryAgain()
    }

    this.focusPinField()

    if (!isStandalone) {
      window.addEventListener('beforeunload', this.handleBeforeUnload)
    }
  }

  componentWillUnmount() {
    if (!isStandalone) {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }
  }

  /**
   * Prevent page reload while logging in.
   *
   * @param event
   */
  handleBeforeUnload = event => {
    if (this.state.submitting || this.props.areAppActionsDisabled) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  focusPinField = () => {
    setTimeout(() => {
      try {
        this.pinFieldRef.current.input.focus()
      } catch (e) {
        console.log(e)
      }
    }, 100)
  }

  getTermsLink = () => {
    try {
      const { intl, terms } = this.props

      // Or should never happen but just in case local storage doesn't get updated when new update comes
      if (terms) {
        // Set french terms link if available
        if (intl.locale === 'fr' && terms.link_fr) {
          return terms.link_fr
        } else {
          return terms.link
        }
      }
    } catch (e) {
      console.log(e)
    }

    return 'https://clearpath.insurevault.com/vault/terms'
  }

  handleEmailSubmittion = values => {
    const email = values['email'].toLowerCase()
    this.handleLoginRequest(email, true)
  }

  handleLoginRequest = (
    email,
    checkRelatedAccounts = false,
    submittedEmail = null,
    vaultUserAccountId = null,
  ) => {
    const { intl, setEmail, setLanguage, toast } = this.props

    this.setState({
      submitting: true,
      email,
    })

    postRequest(
      {
        base: '/tokens/loginRequest',
        mock: false,
      },
      {
        email,
        checkRelatedAccounts,
        submittedEmail,
        vaultUserAccountId,
        type: this.state.usePin ? 'pin' : 'token',
      },
    )
      .then(response => {
        // If user wanting to login has multiple accounts, show list of accounts to choose from
        const multipleAccounts =
          response &&
          response.hasOwnProperty('multipleAccounts') &&
          response.multipleAccounts.length > 0
            ? response.multipleAccounts
            : null

        if (multipleAccounts) {
          this.setState({
            submitting: false,
            multipleAccounts: multipleAccounts,
          })
        } else {
          setEmail(email)
          this.setState(
            {
              emailSent: true,
              submitting: false,
              isEmailJustSent: true,
              multipleAccounts: null,
            },
            () => {
              // Currently not sent from backend, but it might be useful
              const updateLanguage =
                response &&
                response.hasOwnProperty('updateLanguage') &&
                response.updateLanguage.length > 0
                  ? response.updateLanguage
                  : null

              if (updateLanguage) {
                setLanguage(updateLanguage, true, 'authRequest')
              }

              this.focusPinField()
            },
          )
        }
      })
      .catch(e => {
        this.setState({
          submitting: false,
        })
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description: getErrorMessage(
            e,
            intl.formatMessage(intlMessages.authLoginNotificationUnableToSendEmail),
          ),
          status: 'warning',
        })
      })
  }

  pinSubmitError = (defaultError, logData = null, caughtError = null) => {
    const { intl, setAppActionsDisabledState, toast } = this.props

    // Get error from response or take default one
    const getError = getErrorMessage(caughtError, defaultError)

    // Log caught response error (except for missing PIN, since that's not proper error) - this error returns code: 410
    if (caughtError && getErrorCode(caughtError) !== 410) {
      logError(getError, caughtError)
    }

    // Log problems with successful response
    if (logData) {
      logError(defaultError, logData)
    }

    // Enable global app actions (menus, etc.)
    setAppActionsDisabledState(false)

    this.setState({
      submitting: false,
    })
    toast({
      title: intl.formatMessage(intlMessagesApp.appErrorTitle),
      description: getError,
      status: 'error',
    })

    return false
  }

  handlePINSubmittion = values => {
    const {
      intl,
      history,
      email,
      clearEmail,
      setSettings,
      resetUser,
      resetApp,
      setUser,
      isChatEmpty,
      isWidgetState,
      setUserLastAccessType,
      setAppActionsDisabledState,
      updateChatSessionState,
      setFeatures,
    } = this.props

    this.setState(
      {
        submitting: true,
      },
      () => {
        // Disable global app actions (menus, etc.)
        setAppActionsDisabledState(true)
      },
    )

    const chatChannelName = getChatChannelName()

    const pin = values['pin']

    postRequest(
      {
        base: '/tokens/login',
        mock: false,
      },
      {
        payload: pin,
        email: encodeURIComponent(email),
        chatChannel:
          !isChatEmpty && chatChannelName.length ? encodeURIComponent(chatChannelName) : null,
      },
    )
      .then(async response => {
        const logData = { email, response }

        if (!response) {
          return this.pinSubmitError(
            intl.formatMessage(intlMessages.authLoginNotificationUnableToLoginNoResponse),
            logData,
          )
        }

        const { token, user, chatVisitorIdentity, chatChannelUniqueName } = response

        if (!token) {
          return this.pinSubmitError(
            intl.formatMessage(intlMessages.authLoginNotificationUnableToLoginNoToken),
            logData,
          )
        }
        if (!user) {
          return this.pinSubmitError(
            intl.formatMessage(intlMessages.authLoginNotificationUnableToLoginNoUser),
            logData,
          )
        }

        try {
          resetApp('logInPin', isWidgetState, history)
          resetUser()
          clearEmail()
        } catch (e) {
          logError('Unable to reset app', e)
        }

        setLoginToken(token)

        try {
          setUser(user)

          setUserLastAccessType('logInPin')

          await EventsService.registerEvent({ isWidget: isWidgetState }, 'access', 'logInPin')
        } catch (e) {
          logError('Unable to update user', e)
        }

        // If user logging in has existing/older chat
        if (chatVisitorIdentity && chatChannelUniqueName /* && isChatEmpty === true*/) {
          // Set existing/older chat channel name
          setChatChannelName(chatChannelUniqueName, user, isChatEmpty)

          // Set existing/older chat identity
          // We need to set this even thought we're not updating it in database or on twilio side,
          // but it needs to match the channel we're loading
          setChatIdentity(chatVisitorIdentity, user, isChatEmpty)
        } else {
          // Update existing chat channel name and identity (to become unlimited - cookie not expiring)
          updateChatChannelName(user, isChatEmpty)
          updateChatIdentity(user, isChatEmpty)
        }

        // Needed for showing/hiding chat (e.g. if "hide after working hours" is used)
        updateChatSessionState(user, isChatEmpty, false)

        try {
          const settings = await getBrandSettings('a')
          setSettings(settings)
        } catch (e) {
          logError('Unable to fetch settings', e)
        }

        try {
          const features = await getFeatures()
          setFeatures(features)
        } catch (e) {
          logError('Unable to fetch features', e)
        }

        // Enable global app actions (menus, etc.)
        setAppActionsDisabledState(false)

        const redirectUrl = getRedirectUrl()
        if (redirectUrl) {
          removeRedirectUrl()
          history.push(redirectUrl)
        } else {
          history.push('/')
        }

        return true
      })
      .catch(e => {
        const errorCode = getErrorCode(e)
        if (errorCode === 411) {
          this.tryAgain()
        }

        // Show error popup and log error
        this.pinSubmitError(
          intl.formatMessage(intlMessages.authLoginNotificationUnableToLogin),
          null,
          e,
        )
      })
  }

  /**
   * Doesn't actually send email, just shows email field again
   */
  tryAgain = () => {
    const { clearEmail } = this.props

    clearEmail()

    this.setState({
      emailSent: false,
      isEmailJustSent: false,
    })
  }

  renderTryAgainEmail = () => {
    return (
      <Content>
        <Text align={'center'}>
          <FormattedMessage
            id='app.email.problemReceivingEmail'
            defaultMessage='Problems receiving email? '
          />
          &nbsp;
          <b
            className='kiosk-color--desktop-brand-color'
            style={{ cursor: 'pointer' }}
            onClick={this.tryAgain}
          >
            <FormattedMessage id='app.email.tryAgain' defaultMessage='Try again' />
          </b>
        </Text>
      </Content>
    )
  }

  renderFooter = () => {
    const { submitting } = this.state

    return (
      <Footer>
        <Button
          isFullWidth
          loadingText='Submit'
          type='submit'
          isLoading={submitting}
          data-cy='submit'
        >
          <FormattedMessage id='app.email.submit' defaultMessage='Submit' />
        </Button>
      </Footer>
    )
  }

  renderPinInput = () => {
    const { intl, pageBackground } = this.props

    const validatePINSubmittion = values => {
      const errors = {}
      if (!values.pin) {
        errors.pin = intl.formatMessage(intlMessages.authLoginValidationPinRequired)
      }
      if (values.pin && values.pin.length > 5) {
        errors.pin = intl.formatMessage(intlMessages.authLoginValidationPinTooLong)
      }
      if (values.pin && !NUMBER_REGEX.test(values.pin)) {
        errors.pin = intl.formatMessage(intlMessages.authLoginValidationPinNotNumeric)
      }
      return errors
    }

    const classNames = cx({
      [styles.pinInput]: true,
      [styles.pinInputContrast]: isColorRedOrVariant(pageBackground),
    })

    return (
      <Form
        mt={'20px'}
        onSubmit={this.handlePINSubmittion}
        validate={validatePINSubmittion}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Content>
              <Box mb={'48px'} mt={'40px'}>
                <Text variant='secondary' fontSize={'14px'} align={'center'}>
                  <FormattedMessage
                    id='app.email.pinSent'
                    defaultMessage='The PIN has been sent to your email address. Please enter the PIN here to log in:'
                  />
                </Text>
              </Box>
              <Field
                name='pin'
                render={({ input, meta }) => (
                  <Input
                    classNameHolder={classNames}
                    error={meta}
                    setRef={this.pinFieldRef}
                    name='pin'
                    placeholder={intl.formatMessage(intlMessages.authLoginPlaceholderPin)}
                    type='text'
                    {...input}
                  />
                )}
              />
              <Box mt={'20px'} mb={'30px'}>
                <Text variant='secondary' fontSize={'14px'} align={'center'}>
                  <FormattedMessage
                    id='app.email.problemSendingPin1'
                    defaultMessage='Problems receiving pin code?'
                  />
                  &nbsp;
                  <b
                    className={'kiosk--chakra-colors-accent'}
                    style={{ cursor: 'pointer' }}
                    onClick={this.tryAgain}
                  >
                    <FormattedMessage id='app.email.tryAgain' defaultMessage='Try again' />
                  </b>
                </Text>
              </Box>
            </Content>
            {this.renderFooter()}
          </form>
        )}
      />
    )
  }

  getAccountsList = () => {
    const { multipleAccounts } = this.state

    if (!(multipleAccounts && multipleAccounts.length > 0)) {
      return []
    }
    return multipleAccounts.map(account => {
      return {
        listItemId: account.vaultUserAccountId || null,
        listItemEmail: account.email,
        listItemSubmittedEmail: account.submittedEmail || null,
        name: account.vaultUserAccountName || account.name || '-',
        description: account.address || null,
        rightIcon: 'right',
      }
    })
  }

  renderAccountSelection = () => {
    if (this.state.submitting === true) {
      return <Loader styleVariation='staticWithHeight' />
    }

    return (
      <div>
        <div className={styles.multipleAccountsMessage}>
          <FormattedMessage
            id='app.email.multipleAccountsMessage'
            defaultMessage='You are registered on multiple accounts. Please select one.'
          />
        </div>
        <div>
          <List
            attrDataQa='list-login-multiple-accounts'
            items={this.getAccountsList()}
            onClick={this.handleAccountSelectionClick}
            classNameListItem={styles.listItem}
            classNameListItemText={styles.listItemText}
            textsWhiteSpace='wrap'
          />
        </div>
      </div>
    )
  }

  handleAccountSelectionClick = account => {
    if (account && account.listItemEmail) {
      this.handleLoginRequest(
        account.listItemEmail,
        false,
        account.listItemSubmittedEmail,
        account.listItemId,
      )
    } else {
      logError('Could not find Account Email')
    }
  }

  renderEmailInput = () => {
    const { intl, pageBackground } = this.props

    if (document.location.protocol === 'http:' && process.env.REACT_APP_ENV !== 'development') {
      return (
        <Text fontSize={14} align={'center'}>
          <FormattedMessage
            id='app.email.beOnSecurePage'
            defaultMessage='You need to be on a secure page to login'
          />
        </Text>
      )
    }
    const validateEmailSubmittion = values => {
      const errors = {}
      if (!values.email) {
        errors.email = intl.formatMessage(intlMessages.authLoginValidationEmailRequired)
      }
      if (values.email && !validateEmail(values.email)) {
        errors.email = intl.formatMessage(intlMessages.authLoginValidationEmailNotValid)
      }
      if (!values.termsOfService) {
        errors.termsOfService = intl.formatMessage(intlMessages.authLoginValidationTermsOfService)
      }
      return errors
    }

    const classNamesEmail = cx({
      [styles.emailField]: true,
      [styles.emailFieldContrast]: isColorRedOrVariant(pageBackground),
    })

    const classNamesTerms = cx({
      [styles.emailTermsContrast]: isColorRedOrVariant(pageBackground),
    })

    return (
      <Form
        mt={'20px'}
        onSubmit={this.handleEmailSubmittion}
        validate={validateEmailSubmittion}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Content>
              <Box mb={'48px'}>
                <Text variant='secondary' fontSize={'14px'} className={styles.emailDescription}>
                  <FormattedMessage
                    id='app.email.emailAddressExplanation'
                    defaultMessage="Please enter the email address that's on your file with us, and you will receive an email that gives you access to your documents."
                  />
                </Text>
              </Box>
              <Field
                name='email'
                render={({ input, meta }) => (
                  <Input
                    error={meta}
                    classNameHolder={classNamesEmail}
                    placeholder={intl.formatMessage(intlMessages.authLoginPlaceholderEmail)}
                    type='text'
                    {...input}
                  />
                )}
              />
              <Box mb={'30px'} style={{ textAlign: 'center' }}>
                <Field
                  name='termsOfService'
                  render={({ input, meta }) => (
                    <Checkbox
                      error={meta}
                      classNameHolder={classNamesTerms}
                      label={
                        <Text variant='secondary' fontSize={'14px'}>
                          <FormattedHTMLMessage
                            id='app.email.termsOfService'
                            values={{
                              url: this.getTermsLink(),
                            }}
                            defaultMessage={
                              'I have read the <a href="{url}" class="kiosk--chakra-colors-accent" target="_blank" rel="noopener noreferrer">Terms of Service</a>'
                            }
                          />
                        </Text>
                      }
                      {...input}
                    ></Checkbox>
                  )}
                />
              </Box>
            </Content>
            {this.renderFooter()}
          </form>
        )}
      />
    )
  }

  handleBack = () => {
    // Go home instead of back
    this.props.history.push('/')
  }

  render() {
    const { emailSent, isEmailJustSent, usePin, multipleAccounts } = this.state
    const { user, intl, areAppActionsDisabled } = this.props

    const isLoginStillInProgress = isLoggedIn(user) && areAppActionsDisabled === true

    return (
      <Layout className={styles.componentBackground + 'kiosk--chakra-colors-background'}>
        {/* todo add useDefaultDocumentTitle */}
        <PageTitle title={intl.formatMessage(intlMessages.authLoginHeadline)} showBack={true} />
        {multipleAccounts && multipleAccounts.length > 0 ? (
          <Container>{this.renderAccountSelection()}</Container>
        ) : (
          <Container padder className='global-centered-desktop-content'>
            {!emailSent || (!isEmailJustSent && !usePin) ? (
              isLoginStillInProgress ? (
                <Loader styleVariation='staticWithHeight' />
              ) : (
                this.renderEmailInput()
              )
            ) : (
              ''
            )}

            {emailSent && usePin && this.renderPinInput()}

            {isEmailJustSent && !usePin && (
              <div style={{ textAlign: 'center' }}>
                <h2>
                  <FormattedHTMLMessage
                    id='app.email.successEmailSentH2Tag'
                    defaultMessage='Thank you'
                  />
                </h2>
                <p>
                  <FormattedHTMLMessage
                    id='app.email.successEmailSentPTag'
                    defaultMessage='An email with instructions was sent to you, please check your inbox'
                  />
                </p>
                {this.renderTryAgainEmail()}
              </div>
            )}
          </Container>
        )}
        {/* <NavBottom /> */}
      </Layout>
    )
  }
}

const EmailContext = props => (
  <WidgetContext.Consumer>{context => <Email {...props} {...context} />}</WidgetContext.Consumer>
)

export default withRouter(injectIntl(withToast(EmailContext)))
